import { useId, type ReactNode } from 'react'
import { useFocusTrap } from '../../hooks/useFocusTrap'
import styles from './ActionConfirmModal.module.css'

interface ActionConfirmModalProps {
  isOpen: boolean
  title: string
  message?: string
  children?: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  tone?: 'default' | 'danger'
  isLoading?: boolean
  /** Keeps the confirm button disabled, e.g. until a typed confirmation matches. */
  confirmDisabled?: boolean
  onConfirm: () => void
  onCancel: () => void
}

export function ActionConfirmModal({
  isOpen,
  title,
  message,
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'default',
  isLoading = false,
  confirmDisabled = false,
  onConfirm,
  onCancel,
}: ActionConfirmModalProps) {
  const titleId = useId()
  const { containerRef } = useFocusTrap({
    isActive: isOpen,
    onEscape: () => {
      if (!isLoading) onCancel()
    },
  })

  if (!isOpen) {
    return null
  }

  return (
    <div className={styles.backdrop} onClick={() => !isLoading && onCancel()} role="presentation">
      <div
        ref={containerRef}
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(event) => event.stopPropagation()}
      >
        <div className={styles.header}>
          <h3 id={titleId} className={styles.title}>{title}</h3>
        </div>
        <div className={styles.body}>
          {message ? <p className={styles.message}>{message}</p> : null}
          {children}
        </div>
        <div className={styles.actions}>
          <button type="button" className={styles.cancelButton} onClick={onCancel} disabled={isLoading}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className={tone === 'danger' ? styles.dangerButton : styles.confirmButton}
            onClick={onConfirm}
            disabled={isLoading || confirmDisabled}
          >
            {isLoading ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
